import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useProject } from '../context/ProjectContext';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Slider } from './ui/slider';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Camera, Save, AlertTriangle, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { useUnsavedBlocker, BlockerDialog } from './UnsavedChangesDialog';

export const SpecificationsPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { currentProject, fetchProject, updateProject, uploadImage } = useProject();
  const fileInputRef = useRef(null);
  
  const [specs, setSpecs] = useState({
    name: '',
    project_life_years: 5,
    logo: null,
    operates_seven_days: true,
    closed_days: [],
    currency: 'MXN'
  });
  
  const [showWarningDialog, setShowWarningDialog] = useState(false);
  const [warningType, setWarningType] = useState(null);
  const [warningAcknowledged, setWarningAcknowledged] = useState(false);
  const [pendingYears, setPendingYears] = useState(null);
  const [saving, setSaving] = useState(false);
  const [initialSpecs, setInitialSpecs] = useState(null);

  useEffect(() => {
    const loadProject = async () => {
      if (!currentProject || currentProject.id !== projectId) {
        await fetchProject(projectId);
      }
    };
    loadProject();
  }, [projectId, currentProject, fetchProject]);

  useEffect(() => {
    if (currentProject?.specifications) {
      const s = {
        name: currentProject.specifications.name || currentProject.name || '',
        project_life_years: currentProject.specifications.project_life_years || 5,
        logo: currentProject.specifications.logo || null,
        operates_seven_days: currentProject.specifications.operates_seven_days ?? true,
        closed_days: currentProject.specifications.closed_days || [],
        currency: currentProject.specifications.currency || 'MXN'
      };
      setSpecs(s);
      setInitialSpecs(JSON.stringify(s));
    }
  }, [currentProject]);

  const hasUnsavedChanges = initialSpecs && JSON.stringify(specs) !== initialSpecs;

  const handleYearsChange = (value) => {
    const years = value[0];
    
    if (!warningAcknowledged && years >= 1 && years <= 2) {
      setWarningType('short');
      setPendingYears(years);
      setShowWarningDialog(true);
    } else if (!warningAcknowledged && years >= 6) {
      setWarningType('long');
      setPendingYears(years);
      setShowWarningDialog(true);
    } else {
      setSpecs(prev => ({ ...prev, project_life_years: years }));
    }
  };

  const confirmYearsChange = () => {
    setSpecs(prev => ({ ...prev, project_life_years: pendingYears }));
    setShowWarningDialog(false);
    setPendingYears(null);
    setWarningAcknowledged(true);
  };

  const cancelYearsChange = () => {
    setShowWarningDialog(false);
    setPendingYears(null);
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Por favor selecciona una imagen');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('La imagen debe ser menor a 5MB');
      return;
    }

    try {
      const imageData = await uploadImage(file);
      setSpecs(prev => ({ ...prev, logo: imageData }));
      toast.success('Logo cargado exitosamente');
    } catch (err) {
      toast.error('Error al cargar el logo');
    }
  };

  const handleSave = async () => {
    if (!specs.name.trim()) {
      toast.error('Por favor ingresa el nombre del proyecto');
      return;
    }

    setSaving(true);
    try {
      await updateProject(projectId, {
        name: specs.name,
        specifications: specs
      });
      setInitialSpecs(JSON.stringify(specs));
      toast.success('Especificaciones guardadas');
    } catch (err) {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const { showDialog, tryNavigate, handleSaveAndProceed, handleDiscard, handleCancel } = useUnsavedBlocker(hasUnsavedChanges, handleSave);

  const goToNext = async () => {
    if (!tryNavigate(`/app/project/${projectId}/investment`)) {
      await handleSave();
      navigate(`/app/project/${projectId}/investment`);
    }
  };

  const days = [
    { key: 'monday', label: 'Lun' },
    { key: 'tuesday', label: 'Mar' },
    { key: 'wednesday', label: 'Mié' },
    { key: 'thursday', label: 'Jue' },
    { key: 'friday', label: 'Vie' },
    { key: 'saturday', label: 'Sáb' },
    { key: 'sunday', label: 'Dom' }
  ];

  const toggleDay = (day) => {
    setSpecs(prev => {
      const isCurrentlyClosed = prev.closed_days.includes(day);
      return {
        ...prev,
        closed_days: isCurrentlyClosed 
          ? prev.closed_days.filter(d => d !== day)
          : [...prev.closed_days, day],
        operates_seven_days: isCurrentlyClosed 
          ? prev.closed_days.length === 1 
          : false
      };
    });
  };

  return (
    <div className="max-w-3xl mx-auto space-y-8 animate-fade-in" data-testid="specifications-page">
      {/* Progress indicator */}
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <span className="px-2 py-1 bg-primary/20 text-primary rounded text-xs font-medium">PASO 1 DE 10</span>
      </div>

      <div>
        <h1 className="text-4xl font-bold mb-2">Especificaciones del Proyecto</h1>
        <p className="text-muted-foreground text-lg">
          Define los parámetros base y datos fundamentales para tu modelo financiero.
        </p>
      </div>

      {/* Logo Upload */}
      <Card className="overflow-hidden">
        <CardContent className="p-8">
          <div className="flex items-center gap-8">
            <div className="relative group">
              <div 
                className={cn(
                  "w-28 h-28 rounded-full flex items-center justify-center overflow-hidden",
                  "bg-secondary border-2 border-dashed border-border",
                  "cursor-pointer transition-all hover:border-primary"
                )}
                onClick={() => fileInputRef.current?.click()}
                data-testid="logo-upload-area"
              >
                {specs.logo ? (
                  <img src={specs.logo} alt="Logo" className="w-full h-full object-cover" />
                ) : (
                  <Camera className="w-8 h-8 text-muted-foreground" />
                )}
              </div>
              <button 
                className="absolute -bottom-1 -right-1 w-8 h-8 bg-primary rounded-full flex items-center justify-center shadow-lg hover:bg-primary/90 transition-colors"
                onClick={() => fileInputRef.current?.click()}
              >
                <Camera className="w-4 h-4 text-primary-foreground" />
              </button>
              <input 
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleLogoUpload}
              />
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">Logo del Proyecto</h3>
              <p className="text-muted-foreground text-sm mb-3">
                Sube un logo circular para identificar el proyecto (PNG, JPG, máx 5MB)
              </p>
              <Button 
                variant="secondary" 
                size="sm"
                onClick={() => fileInputRef.current?.click()}
              >
                Seleccionar Archivo
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Project Name */}
      <Card>
        <CardContent className="p-8">
          <Label htmlFor="project-name" className="text-base font-medium mb-3 block">
            Nombre del Proyecto
          </Label>
          <Input
            id="project-name"
            placeholder="Ej: Expansión Comercial 2024"
            value={specs.name}
            onChange={(e) => setSpecs(prev => ({ ...prev, name: e.target.value }))}
            className="text-lg h-14 bg-secondary/50"
            data-testid="project-name-input"
          />
        </CardContent>
      </Card>

      {/* Operating Days */}
      <Card>
        <CardContent className="p-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-semibold">Operación 7 Días</h3>
              <p className="text-sm text-muted-foreground">
                Incluir fines de semana en cálculos de ingresos y costos diarios
              </p>
            </div>
            <Switch
              checked={specs.operates_seven_days}
              onCheckedChange={(checked) => {
                setSpecs(prev => ({ 
                  ...prev, 
                  operates_seven_days: checked,
                  closed_days: checked ? [] : prev.closed_days
                }));
              }}
              data-testid="seven-days-switch"
            />
          </div>

          {!specs.operates_seven_days && (
            <div className="pt-4 border-t border-border/50">
              <Label className="text-sm text-muted-foreground mb-3 block">
                Días de cierre:
              </Label>
              <div className="flex gap-2">
                {days.map((day) => (
                  <button
                    key={day.key}
                    onClick={() => toggleDay(day.key)}
                    className={cn(
                      "w-12 h-12 rounded-lg text-sm font-medium transition-all",
                      specs.closed_days.includes(day.key)
                        ? "bg-destructive/20 text-destructive border-2 border-destructive"
                        : "bg-secondary text-foreground hover:bg-secondary/80"
                    )}
                    data-testid={`day-${day.key}`}
                  >
                    {day.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Project Life */}
      <Card>
        <CardContent className="p-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-semibold">Vida del Proyecto (Horizonte de Inversión)</h3>
              <p className="text-sm text-muted-foreground">
                Duración de la simulación financiera en años
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="secondary"
                size="icon"
                onClick={() => handleYearsChange([Math.max(1, specs.project_life_years - 1)])}
                disabled={specs.project_life_years <= 1}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-4xl font-bold w-12 text-center">
                {specs.project_life_years}
              </span>
              <Button
                variant="secondary"
                size="icon"
                onClick={() => handleYearsChange([Math.min(20, specs.project_life_years + 1)])}
                disabled={specs.project_life_years >= 20}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>

          <div className="space-y-4">
            <Slider
              value={[specs.project_life_years]}
              onValueChange={handleYearsChange}
              min={1}
              max={20}
              step={1}
              className="py-4"
              data-testid="project-life-slider"
            />
            
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>1 AÑO</span>
              <div className="flex items-center gap-1">
                <span className={cn(
                  "px-2 py-0.5 rounded text-xs",
                  specs.project_life_years >= 3 && specs.project_life_years <= 5 
                    ? "bg-success/20 text-success" 
                    : "bg-muted text-muted-foreground"
                )}>
                  HORIZONTE IDEAL (3-5 AÑOS)
                </span>
              </div>
              <span>20 AÑOS</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Currency Selection */}
      <Card>
        <CardContent className="p-8">
          <Label className="text-base font-medium mb-3 block">Moneda</Label>
          <div className="flex gap-3">
            {['MXN', 'USD'].map((curr) => (
              <Button
                key={curr}
                variant={specs.currency === curr ? 'default' : 'outline'}
                onClick={() => setSpecs(prev => ({ ...prev, currency: curr }))}
                className="flex-1"
                data-testid={`currency-${curr}`}
              >
                {curr === 'MXN' ? '🇲🇽 MXN - Peso Mexicano' : '🇺🇸 USD - Dólar Americano'}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Working Capital */}
      <Card>
        <CardContent className="p-8">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h3 className="text-lg font-semibold">Capital de Trabajo</h3>
              <p className="text-sm text-muted-foreground">
                Porcentaje de los ingresos que se destina a capital de trabajo (cuentas por cobrar, inventario, etc.)
              </p>
            </div>
            <div className="flex items-center gap-2 min-w-32">
              <Input
                type="number"
                min="0"
                max="50"
                step="0.5"
                value={specs.working_capital_pct ?? 5}
                onChange={(e) => setSpecs(prev => ({ ...prev, working_capital_pct: parseFloat(e.target.value) || 0 }))}
                className="w-20 text-center text-lg font-bold"
                data-testid="working-capital-input"
              />
              <span className="text-lg font-semibold text-muted-foreground">%</span>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Valor estándar: 5-10% de ingresos. Se calcula como cambio incremental año a año en el flujo de efectivo.
          </p>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex justify-between">
        <Button variant="outline" onClick={() => navigate('/app')}>
          Cancelar
        </Button>
        <div className="flex gap-3">
          <Button variant="secondary" onClick={handleSave} disabled={saving} data-testid="save-draft-btn">
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Guardando...' : 'Guardar Borrador'}
          </Button>
          <Button onClick={goToNext} data-testid="continue-btn">
            Continuar
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>

      {/* Warning Dialog */}
      <Dialog open={showWarningDialog} onOpenChange={setShowWarningDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-warning" />
              Advertencia sobre el horizonte de tiempo
            </DialogTitle>
            <DialogDescription className="pt-4">
              {warningType === 'short' ? (
                <>
                  <p className="mb-4">
                    Un horizonte de <strong>{pendingYears} año(s)</strong> es muy corto para un proyecto de inversión.
                    Lo ideal es entre <strong>3 y 5 años</strong> para obtener proyecciones financieras significativas.
                  </p>
                  <p>¿Deseas continuar con este rango de tiempo?</p>
                </>
              ) : (
                <>
                  <p className="mb-4">
                    Un horizonte de <strong>{pendingYears} años</strong> es demasiado largo y presenta mucha incertidumbre.
                    Lo ideal es entre <strong>3 y 5 años</strong> para mantener proyecciones más precisas.
                  </p>
                  <p>¿Deseas continuar con este rango de tiempo?</p>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={cancelYearsChange}>
              No, ajustar
            </Button>
            <Button onClick={confirmYearsChange} data-testid="confirm-years-btn">
              Sí, continuar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BlockerDialog showDialog={showDialog} onSave={handleSaveAndProceed} onDiscard={handleDiscard} onCancel={handleCancel} saving={saving} />
    </div>
  );
};
